/**
 * UsuarioRepo sobre Supabase. Misma lógica que DexieUsuarioRepo.
 * Habilita el padrón de usuarios COMPARTIDO (login de contratistas/INT
 * contra una sola lista en la nube).
 */

import type { UsuarioRepo } from "../../repos/UsuarioRepo";
import type { Usuario } from "@app-types/entities";
import type { Role } from "@constants/roles";
import { GERENCIA_DEFAULT } from "@constants/gerencias";
import { SupabaseBaseRepo } from "./SupabaseBaseRepo";

function normalizar(u: Usuario): Usuario {
  if (Array.isArray(u.gerenciasAccesibles) && u.gerenciasAccesibles.length > 0) return u;
  return { ...u, gerenciasAccesibles: [GERENCIA_DEFAULT] };
}

export class SupabaseUsuarioRepo extends SupabaseBaseRepo<Usuario> implements UsuarioRepo {
  constructor(tenantId: string) {
    super("usuario", tenantId);
  }

  async list(filter?: Partial<Usuario>): Promise<Usuario[]> {
    const all = (await this.listAll()).map(normalizar);
    if (filter?.rol) return all.filter((u) => u.rol === filter.rol);
    if (filter?.activo !== undefined) return all.filter((u) => u.activo === filter.activo);
    return all;
  }

  async listByRole(role: Role): Promise<Usuario[]> {
    return (await this.listAll()).filter((u) => u.rol === role).map(normalizar);
  }

  async listActive(): Promise<Usuario[]> {
    return (await this.listAll()).filter((u) => u.activo).map(normalizar);
  }

  async getByEmail(email: string): Promise<Usuario | null> {
    const found = (await this.listAll()).find((u) => u.email === email);
    return found ? normalizar(found) : null;
  }

  async get(id: string): Promise<Usuario | null> {
    const item = await this.getById(id);
    return item ? normalizar(item) : null;
  }

  async create(data: Omit<Usuario, "id" | "createdAt" | "updatedAt" | "version">): Promise<string> {
    const existing = await this.getByEmail(data.email);
    if (existing) throw new Error(`Ya existe un usuario con email ${data.email}`);
    const now = new Date().toISOString();
    const newUser: Usuario = {
      ...data,
      id: crypto.randomUUID(),
      tenantId: this.tenantId,
      createdAt: now,
      updatedAt: now,
      version: 1,
    };
    await this.createRaw(newUser);
    return newUser.id;
  }

  async update(id: string, data: Partial<Usuario>, expectedVersion: number): Promise<void> {
    const current = await this.get(id);
    if (!current) throw new Error(`Usuario ${id} not found`);
    const updated: Usuario = {
      ...current,
      ...data,
      // Auditoria de creacion INMUTABLE: ver BaseRepo.update.
      createdBy: current.createdBy,
      createdAt: current.createdAt,
      version: current.version + 1,
      updatedAt: new Date().toISOString(),
    };
    await this.updateRaw(updated, expectedVersion);
  }

  async setActive(id: string, active: boolean): Promise<void> {
    const current = await this.get(id);
    if (!current) throw new Error(`Usuario ${id} not found`);
    await this.update(id, { activo: active }, current.version);
  }

  async setDefault(id: string, esDefault: boolean, userEmail: string): Promise<void> {
    const target = await this.get(id);
    if (!target) throw new Error(`Usuario ${id} not found`);
    const now = new Date().toISOString();
    if (esDefault) {
      const mismosRol = await this.listByRole(target.rol);
      const otrosDefault = mismosRol.filter((u) => u.id !== id && u.esDefault);
      for (const u of otrosDefault) {
        await this.putRaw({ ...u, esDefault: false, version: u.version + 1, updatedAt: now, updatedBy: userEmail });
      }
      await this.putRaw({ ...target, esDefault: true, version: target.version + 1, updatedAt: now, updatedBy: userEmail });
    } else {
      const updated: Usuario = { ...target, version: target.version + 1, updatedAt: now, updatedBy: userEmail };
      delete updated.esDefault;
      await this.putRaw(updated);
    }
  }

  async delete(id: string): Promise<void> {
    await this.deleteById(id);
  }
}
